import type { ChatSidebarBlock } from "../types/chat-types.ts";
import { sanitizeMessageText } from "./message-extract.ts";

export type JsonPreview = {
  parsed: unknown;
  pretty: string;
};

const MAX_JSON_AUTOPARSE_CHARS = 20_000;
const MAX_RICH_AUTODETECT_CHARS = 80_000;
const MAX_RICH_BLOCKS = 32;
const FENCED_BLOCK_RE = /```([^\n`]*)[ \t]*\r?\n([\s\S]*?)\r?\n```/g;
const EMBED_DIRECTIVE_RE = /^::(iframe|h5)\[\s*url\s*=\s*("[^"]+"|'[^']+'|[^\]]+?)\s*\]$/i;
const MERMAID_BLOCK_PATTERNS = [
  /^(graph|flowchart)\b/i,
  /^sequenceDiagram\b/i,
  /^classDiagram\b/i,
  /^stateDiagram(?:-v2)?\b/i,
  /^erDiagram\b/i,
  /^journey\b/i,
  /^gantt\b/i,
  /^pie\b/i,
  /^mindmap\b/i,
  /^timeline\b/i,
  /^gitGraph\b/i,
  /^quadrantChart\b/i,
  /^requirementDiagram\b/i,
  /^block-beta\b/i,
  /^packet-beta\b/i,
  /^architecture-beta\b/i,
  /^sankey-beta\b/i,
  /^xychart-beta\b/i,
];
const DIRECT_RICH_CONTENT_TYPES = new Set([
  "diagram-mermaid",
  "mermaid",
  "chart-vega-lite",
  "vega",
  "vega-lite",
  "vegalite",
  "math-latex",
  "math",
  "latex",
  "embedded-markdown",
  "embedded_markdown",
  "markdown-embed",
  "markdown_embed",
  "svg",
  "iframe",
  "h5",
  "json",
]);
const RICH_BLOCK_TYPES = new Set<ChatSidebarBlock["type"]>([
  "diagram-mermaid",
  "chart-vega-lite",
  "math-latex",
  "embedded-markdown",
  "svg",
  "iframe",
]);

export function detectJsonPreview(text: string): JsonPreview | null {
  const trimmed = text.trim();
  if (trimmed.length > MAX_JSON_AUTOPARSE_CHARS) {
    return null;
  }
  if (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    try {
      const parsed = JSON.parse(trimmed);
      return { parsed, pretty: JSON.stringify(parsed, null, 2) };
    } catch {
      return null;
    }
  }
  return null;
}

export function buildRichBlocksFromText(text: string | null | undefined): ChatSidebarBlock[] {
  if (!text?.trim()) {
    return [];
  }

  const blocks: ChatSidebarBlock[] = [];
  let cursor = 0;
  FENCED_BLOCK_RE.lastIndex = 0;

  for (const match of text.matchAll(FENCED_BLOCK_RE)) {
    const blockStart = match.index ?? 0;
    pushMarkdownSegment(blocks, text.slice(cursor, blockStart));

    const [fullMatch, language, source] = match;
    const explicitBlock = normalizeExplicitFenceBlock(language ?? "", source ?? "");
    if (explicitBlock) {
      blocks.push(explicitBlock);
    } else {
      pushMarkdownSegment(blocks, fullMatch);
    }

    cursor = blockStart + fullMatch.length;
  }

  pushMarkdownSegment(blocks, text.slice(cursor));
  return compactBlocks(blocks);
}

export function buildRichBlocksFromMessage(message: unknown): ChatSidebarBlock[] {
  if (!message || typeof message !== "object") {
    return [];
  }

  const record = message as Record<string, unknown>;
  const content = record.content;
  const role = typeof record.role === "string" ? record.role : "";
  const blocks: ChatSidebarBlock[] = [];
  const textSegments: string[] = [];

  const flushTextSegments = () => {
    if (textSegments.length === 0) {
      return;
    }
    const sanitizedText = sanitizeMessageText(textSegments.join("\n"), role);
    textSegments.length = 0;
    if (sanitizedText.trim()) {
      blocks.push(...buildRichBlocksFromText(sanitizedText));
    }
  };

  if (typeof content === "string") {
    return buildRichBlocksFromText(sanitizeMessageText(content, role));
  }

  if (Array.isArray(content)) {
    for (const item of content) {
      if (!item || typeof item !== "object") {
        continue;
      }
      const entry = item as Record<string, unknown>;
      const type = typeof entry.type === "string" ? entry.type.trim().toLowerCase() : "";
      if (type === "text" && typeof entry.text === "string") {
        textSegments.push(entry.text);
        continue;
      }
      flushTextSegments();
      if (!DIRECT_RICH_CONTENT_TYPES.has(type)) {
        continue;
      }
      const structuredBlock = normalizeStructuredContentBlock(type, entry);
      if (structuredBlock) {
        blocks.push(structuredBlock);
      }
    }
    flushTextSegments();
  }

  if (blocks.length > 0) {
    return compactBlocks(blocks);
  }

  if (typeof record.text === "string") {
    return buildRichBlocksFromText(sanitizeMessageText(record.text, role));
  }

  return [];
}

export function containsRichSidebarBlocks(blocks: ChatSidebarBlock[]): boolean {
  return blocks.some((block) => isRichSidebarBlock(block));
}

export function isRichSidebarBlock(block: ChatSidebarBlock): boolean {
  return RICH_BLOCK_TYPES.has(block.type);
}

export function buildRawTextFromBlocks(blocks: ChatSidebarBlock[]): string | null {
  const parts = blocks
    .map((block) => chatSidebarBlockToRawText(block))
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0);
  return parts.length > 0 ? parts.join("\n\n") : null;
}

export function chatSidebarBlockToRawText(block: ChatSidebarBlock): string | null {
  switch (block.type) {
    case "markdown":
      return block.content;
    case "json":
      return JSON.stringify(block.data, null, 2);
    case "code":
      return block.language ? `\ \ \ ${block.language}\n${block.content}\n\ \ \ `.replace(/\u0000/g, "`") : block.content;
    case "image":
      return `![${block.alt ?? "Attached image"}](${block.url})`;
    case "diagram-mermaid":
      return `\ \ \ mermaid\n${block.content}\n\ \ \ `.replace(/\u0000/g, "`");
    case "chart-vega-lite":
      return `\ \ \ vega-lite\n${block.content}\n\ \ \ `.replace(/\u0000/g, "`");
    case "math-latex":
      return `$$\n${block.content}\n$$`;
    case "embedded-markdown":
      return `\ \ \ markdown-embed\n${block.content}\n\ \ \ `.replace(/\u0000/g, "`");
    case "svg":
      return `\ \ \ svg\n${block.content}\n\ \ \ `.replace(/\u0000/g, "`");
    case "iframe":
      return `::${block.variant ?? "iframe"}[url=${block.url}]`;
    default:
      return null;
  }
}

function pushMarkdownSegment(blocks: ChatSidebarBlock[], text: string) {
  if (!text.trim()) {
    return;
  }

  const markdownLines = text.replace(/\r\n?/g, "\n").split("\n");
  const buffer: string[] = [];

  const flushBuffer = () => {
    const value = trimBoundaryBlankLines(buffer.join("\n"));
    buffer.length = 0;
    pushHeuristicOrMarkdownBlock(blocks, value);
  };

  for (const line of markdownLines) {
    const directive = parseEmbedDirective(line.trim());
    if (!directive) {
      buffer.push(line);
      continue;
    }
    flushBuffer();
    blocks.push(directive);
  }

  flushBuffer();
}

function pushHeuristicOrMarkdownBlock(blocks: ChatSidebarBlock[], text: string) {
  if (!text.trim()) {
    return;
  }
  const heuristicBlock = detectStandaloneRichBlock(text);
  if (heuristicBlock) {
    blocks.push(heuristicBlock);
    return;
  }
  const jsonPreview = detectJsonPreview(text);
  if (jsonPreview) {
    blocks.push({ type: "json", data: jsonPreview.parsed });
    return;
  }
  blocks.push({ type: "markdown", content: text });
}

function detectStandaloneRichBlock(text: string): ChatSidebarBlock | null {
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > MAX_RICH_AUTODETECT_CHARS) {
    return null;
  }

  const directiveBlock = parseEmbedDirective(trimmed);
  if (directiveBlock) {
    return directiveBlock;
  }

  const mathBlock = detectDisplayMathBlock(trimmed);
  if (mathBlock) {
    return mathBlock;
  }

  const mermaidBlock = detectMermaidHeuristicBlock(trimmed);
  if (mermaidBlock) {
    return mermaidBlock;
  }

  return detectVegaHeuristicBlock(trimmed);
}

function detectDisplayMathBlock(trimmed: string): ChatSidebarBlock | null {
  if (!trimmed.startsWith("$$") || !trimmed.endsWith("$$") || trimmed.length <= 4) {
    return null;
  }
  const content = trimmed.slice(2, -2).trim();
  if (!content) {
    return null;
  }
  return {
    type: "math-latex",
    content,
    displayMode: true,
    label: "LaTeX",
  };
}

function detectMermaidHeuristicBlock(trimmed: string): ChatSidebarBlock | null {
  const firstLine = trimmed.split(/\r?\n/, 1)[0]?.trim() ?? "";
  if (!MERMAID_BLOCK_PATTERNS.some((pattern) => pattern.test(firstLine))) {
    return null;
  }

  if (!trimmed.includes("\n") && !/[;>-]/.test(trimmed)) {
    return null;
  }

  return {
    type: "diagram-mermaid",
    content: trimmed,
    label: "Mermaid",
  };
}

function detectVegaHeuristicBlock(trimmed: string): ChatSidebarBlock | null {
  const jsonPreview = detectJsonPreview(trimmed);
  if (!jsonPreview || !isVegaSchemaSpec(jsonPreview.parsed)) {
    return null;
  }

  return {
    type: "chart-vega-lite",
    content: jsonPreview.pretty,
    spec: jsonPreview.parsed,
    label: "Vega-Lite",
  };
}

function normalizeExplicitFenceBlock(language: string, source: string): ChatSidebarBlock | null {
  const normalizedLanguage = normalizeFenceLanguage(language);
  const content = source.trim();
  if (!content) {
    return null;
  }

  switch (normalizedLanguage) {
    case "mermaid":
      return {
        type: "diagram-mermaid",
        content,
        label: "Mermaid",
      };
    case "vega":
    case "vega-lite":
    case "vegalite": {
      const jsonPreview = detectJsonPreview(content);
      return {
        type: "chart-vega-lite",
        content: jsonPreview?.pretty ?? content,
        spec: jsonPreview?.parsed,
        label: "Vega-Lite",
      };
    }
    case "math":
    case "latex":
      return {
        type: "math-latex",
        content,
        displayMode: true,
        label: "LaTeX",
      };
    case "svg":
      return {
        type: "svg",
        content,
        label: "SVG",
      };
    case "markdown-embed":
    case "markdown-embedded":
    case "embedded-markdown":
    case "markdown_embed":
    case "md-embed":
      return {
        type: "embedded-markdown",
        content,
        label: "Embedded Markdown",
      };
    default:
      return null;
  }
}

function normalizeStructuredContentBlock(
  type: string,
  entry: Record<string, unknown>,
): ChatSidebarBlock | null {
  switch (type) {
    case "diagram-mermaid":
    case "mermaid": {
      const content = getRecordString(entry, ["text", "content", "code", "source", "diagram"]);
      return content
        ? {
            type: "diagram-mermaid",
            content,
            label: "Mermaid",
          }
        : null;
    }
    case "chart-vega-lite":
    case "vega":
    case "vega-lite":
    case "vegalite": {
      const rawSpec = getRecordValue(entry, ["spec", "data", "content", "text"]);
      if (rawSpec === undefined) {
        return null;
      }
      if (typeof rawSpec === "string") {
        const content = rawSpec.trim();
        if (!content) {
          return null;
        }
        const jsonPreview = detectJsonPreview(content);
        return {
          type: "chart-vega-lite",
          content: jsonPreview?.pretty ?? content,
          spec: jsonPreview?.parsed,
          label: "Vega-Lite",
        };
      }
      return {
        type: "chart-vega-lite",
        content: JSON.stringify(rawSpec, null, 2),
        spec: rawSpec,
        label: "Vega-Lite",
      };
    }
    case "math-latex":
    case "math":
    case "latex": {
      const content = getRecordString(entry, ["text", "content", "latex", "source"]);
      return content
        ? {
            type: "math-latex",
            content,
            displayMode: true,
            label: "LaTeX",
          }
        : null;
    }
    case "embedded-markdown":
    case "embedded_markdown":
    case "markdown-embed":
    case "markdown_embed": {
      const content = getRecordString(entry, ["text", "content", "markdown", "source"]);
      return content
        ? {
            type: "embedded-markdown",
            content,
            label: "Embedded Markdown",
          }
        : null;
    }
    case "svg": {
      const content = getRecordString(entry, ["text", "content", "svg", "source"]);
      return content
        ? {
            type: "svg",
            content,
            label: "SVG",
          }
        : null;
    }
    case "iframe":
    case "h5": {
      const url = getRecordString(entry, ["url", "src", "href", "content"]);
      return url
        ? {
            type: "iframe",
            url,
            variant: type === "h5" ? "h5" : "iframe",
            label: type === "h5" ? "H5" : "Iframe",
          }
        : null;
    }
    case "json": {
      const rawValue = getRecordValue(entry, ["data", "content", "text", "value"]);
      if (rawValue === undefined) {
        return null;
      }
      if (typeof rawValue === "string") {
        const chartBlock = detectVegaHeuristicBlock(rawValue.trim());
        if (chartBlock) {
          return chartBlock;
        }
        const jsonPreview = detectJsonPreview(rawValue);
        if (!jsonPreview) {
          return null;
        }
        return {
          type: "json",
          data: jsonPreview.parsed,
        };
      }
      if (isVegaSchemaSpec(rawValue)) {
        return {
          type: "chart-vega-lite",
          content: JSON.stringify(rawValue, null, 2),
          spec: rawValue,
          label: "Vega-Lite",
        };
      }
      return {
        type: "json",
        data: rawValue,
      };
    }
    default:
      return null;
  }
}

function parseEmbedDirective(text: string): Extract<ChatSidebarBlock, { type: "iframe" }> | null {
  const match = text.match(EMBED_DIRECTIVE_RE);
  if (!match) {
    return null;
  }

  const [, variant, rawUrl] = match;
  const url = stripQuotedValue(rawUrl.trim());
  if (!url) {
    return null;
  }

  return {
    type: "iframe",
    url,
    variant: variant.toLowerCase() === "h5" ? "h5" : "iframe",
    label: variant.toLowerCase() === "h5" ? "H5" : "Iframe",
  };
}

function compactBlocks(blocks: ChatSidebarBlock[]): ChatSidebarBlock[] {
  const compacted: ChatSidebarBlock[] = [];
  for (const block of blocks) {
    if (compacted.length >= MAX_RICH_BLOCKS) {
      break;
    }
    if (block.type === "markdown" && !block.content.trim()) {
      continue;
    }
    const previous = compacted.at(-1);
    if (previous?.type === "markdown" && block.type === "markdown") {
      previous.content = `${previous.content}\n${block.content}`;
      continue;
    }
    compacted.push(block);
  }
  return compacted;
}

function normalizeFenceLanguage(language: string): string {
  return language.trim().toLowerCase().replace(/_/g, "-").split(/\s+/, 1)[0] ?? "";
}

function getRecordString(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function getRecordValue(record: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (record[key] !== undefined) {
      return record[key];
    }
  }
  return undefined;
}

function stripQuotedValue(value: string): string {
  if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
    return value.slice(1, -1).trim();
  }
  return value;
}

function trimBoundaryBlankLines(value: string): string {
  return value.replace(/^\n+|\n+$/g, "");
}

function isVegaSchemaSpec(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const schema = (value as Record<string, unknown>).$schema;
  return typeof schema === "string" && /vega(?:-lite)?/i.test(schema);
}
